import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import {
  cartaoDestaque,
  cartaoDestaqueAlerta,
  texto,
} from '#components/finapp/estilo';
import type { ResumoDoMes } from '#components/finapp/motor/mesFinanceiro';

/**
 * Branco esmaecido para o rótulo e as linhas de apoio dentro do card verde.
 *
 * ⚠️ Nunca `cor.tintaFraca` aqui: aquele cinza foi escolhido contra fundo
 * claro e some sobre o verde escuro.
 */
const tintaSobreVerde = 'rgba(255, 255, 255, 0.72)';

type Props = {
  resumo: ResumoDoMes;
  /** `null` = não há mês anterior confiável pra comparar (ver `Inicio.tsx`). */
  livreMesAnterior: number | null;
  /**
   * Celular. Vem de `useResponsive()` em `Inicio.tsx`.
   *
   * ⚠️ Sem isto, o valor e a comparação disputam a mesma linha num telefone e
   * o número-herói quebra no meio ("-R$" numa linha, "26.856,06" na outra) —
   * justo o número que a tela inteira existe pra mostrar.
   */
  estreito?: boolean;
};

/**
 * O número-herói da tela: "livre pra decidir este mês".
 *
 * ⚠️⚠️ `resumo.naoEhGasto.naoClassificado` NÃO é exibido aqui, de propósito.
 * O balde "não é gasto" (transferências · empresa · não classificado) é
 * responsabilidade da Task 9 (`TresColunas.tsx`, "As três colunas e o bloco
 * 'não é gasto'"), que é montada logo abaixo deste componente na `/inicio`.
 * O JSDoc de `ResumoDoMes.naoEhGasto.naoClassificado` (mesFinanceiro.ts) e o
 * `progress.md` da Task 7 (fix round 1, Finding 5) são explícitos: o campo
 * TEM que aparecer em algum lugar da `/inicio` — só não precisa ser AQUI,
 * porque duplicar o bloco entre o Herói (compacto, um número + a conta que
 * o produziu) e a TresColunas (a análise "onde foi o resto do dinheiro")
 * brigaria com o design compacto deste componente sem tornar nada mais
 * visível — a Task 9 já é o lugar certo, e ela é a PRÓXIMA peça montada na
 * tela. Ver o relatório da Task 8 para o registro dessa decisão.
 *
 * ⚠️ Só o número PRINCIPAL leva "R$" (spec §8.4: "R$ 9.412" na linha do
 * herói, mas "62.113" · "1.204" nas linhas de apoio, sem símbolo repetido).
 * Não veio de `useFormat()` (que aplicaria o símbolo da moeda configurada de
 * verdade) de propósito: esse hook depende do Redux (`useSyncedPref`), e o
 * teste deste componente o renderiza SEM Provider — a mesma razão pela qual
 * o Step 3 do brief já usava `integerToCurrency` puro. Como este app é só
 * para um usuário brasileiro (todo o resto da UI já é PT-BR fixo, sem
 * i18n de verdade), o prefixo é literal.
 */
export function Heroi({ resumo, livreMesAnterior, estreito = false }: Props) {
  const { t } = useTranslation();
  const negativo = resumo.livre < 0;
  const fixas = resumo.fixasRealizadas + resumo.fixasPrevistas;
  const diferenca =
    livreMesAnterior === null ? null : resumo.livre - livreMesAnterior;

  return (
    <View
      data-testid="heroi-cartao"
      style={{ ...(negativo ? cartaoDestaqueAlerta : cartaoDestaque), gap: 10 }}
    >
      <Text style={{ ...texto.rotuloCartao, color: tintaSobreVerde }}>
        {t('Livre pra decidir este mês')}
      </Text>

      <View
        style={{
          flexDirection: estreito ? 'column' : 'row',
          alignItems: estreito ? 'flex-start' : 'baseline',
          gap: estreito ? 4 : 16,
        }}
      >
        <Text
          data-testid="heroi-valor"
          style={{
            ...texto.valorGrande,
            ...(estreito ? { fontSize: 34 } : null),
            color: '#ffffff',
            whiteSpace: 'nowrap',
          }}
        >
          {negativo ? '-' : ''}R$ {integerToCurrency(Math.abs(resumo.livre))}
        </Text>

        {diferenca !== null && (
          <Text
            data-testid="heroi-comparacao"
            style={{ ...texto.corpo, color: tintaSobreVerde }}
          >
            {t('vs mês anterior')} {diferenca >= 0 ? '▲' : '▼'}{' '}
            {integerToCurrency(Math.abs(diferenca))}
          </Text>
        )}
      </View>

      <Text
        data-testid="heroi-conta"
        style={{ ...texto.corpo, color: tintaSobreVerde }}
      >
        {t('entrou')} {integerToCurrency(resumo.entrou)} − {t('fixas')}{' '}
        {integerToCurrency(fixas)} − {t('aportes')}{' '}
        {integerToCurrency(resumo.aportes)} − {t('gastos')}{' '}
        {integerToCurrency(resumo.gastosVariaveis)}
      </Text>
    </View>
  );
}
